/** CardForm — renders a card's typed input schema (registry §3) into form controls. */
import type { InputField } from '../../../case-canvas/registry';
import { FileDropzone } from '../../ui/FileDropzone';

export function CardForm({ fields, values, onChange, disabled }: {
  fields: InputField[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2">
      {fields.map((f) => (
        <div key={f.key}>
          {f.kind !== 'toggle' && <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">{f.label}</label>}
          <Control f={f} value={values[f.key]} onChange={(v) => onChange(f.key, v)} disabled={disabled} />
        </div>
      ))}
    </div>
  );
}

function Control({ f, value, onChange, disabled }: { f: InputField; value: unknown; onChange: (v: unknown) => void; disabled?: boolean }) {
  const base = 'w-full text-[11px] px-2 py-1.5 bg-background border border-input rounded disabled:opacity-50';
  switch (f.kind) {
    case 'file':
      return (
        <FileDropzone
          bare
          compact
          accept={f.accept}
          disabled={disabled}
          file={value instanceof File ? value : null}
          onFileChange={(file) => onChange(file)}
          description={f.accept?.replace(/,/g, ' · ') || 'Any file'}
        />
      );
    case 'textarea':
      return <textarea value={String(value ?? '')} placeholder={f.placeholder} disabled={disabled}
        onChange={(e) => onChange(e.target.value)} className={`${base} h-20 font-mono`} />;
    case 'select':
      return <select value={String(value ?? '')} disabled={disabled} onChange={(e) => onChange(e.target.value)} className={base}>
        {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>;
    case 'toggle':
      return <label className="flex items-center gap-2 text-[11px] cursor-pointer">
        <input type="checkbox" checked={value === undefined ? !!f.default : !!value} disabled={disabled}
          onChange={(e) => onChange(e.target.checked)} /> {f.label}
      </label>;
    case 'number':
      return <input type="number" value={value === undefined ? (f.default ?? '') : Number(value)} disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))} className={base} />;
    default:
      return <input type="text" value={String(value ?? '')} placeholder={f.placeholder} disabled={disabled}
        onChange={(e) => onChange(e.target.value)} className={base} />;
  }
}
